import { isAxiosError } from 'axios';
import { authenticatedClient, unauthenticatedClient } from '@/utils/shared/axios';
import { useAuthStore } from '@/stores/auth.store';

let inflight: Promise<void> | null = null;

// Never aborted, e.g. by a navigation: the API replaces the session token on every refresh,
// so a refresh cut short leaves the browser with a token that no longer works.
async function refreshSession() {
  return unauthenticatedClient
    .post<{ token: string }>('/auth/sessions/refresh', {})
    .then(({ data: { token } }) => {
      useAuthStore.getState().login(token);
      console.log('set token', useAuthStore.getState().isAuthenticated);
    })
    .catch((err) => {
      console.log('Session refresh failed', err);
    });
}

export function ensureSessionRefreshed(): Promise<void> {
  if (!inflight) {
    inflight = refreshSession().finally(() => {
      inflight = null;
    });
  }

  return inflight;
}

const CONTEXT_RETRY_DELAYS = [250, 500, 1000, 2000];
const CONTEXT_RETRYABLE = (status?: number) => status === 404 || status === 409 || (status ?? 0) >= 500;

const contextInflight = new Map<string, Promise<void>>();

// Until auth has processed the new membership, switching answers 404, so try again with backoff
async function switchToGroupContext(groupId: string, timeoutMs: number) {
  const deadline = Date.now() + timeoutMs;

  for (let attempt = 0; ; attempt++) {
    if (useAuthStore.getState().context?.groupId === groupId) return;

    try {
      const { data } = await authenticatedClient.put<{ token: string }>('/auth/sessions/context', { groupId });
      useAuthStore.getState().login(data.token);
      return;
    } catch (err) {
      const status = isAxiosError(err) ? err.response?.status : undefined;
      const delay = CONTEXT_RETRY_DELAYS[Math.min(attempt, CONTEXT_RETRY_DELAYS.length - 1)];

      if (!CONTEXT_RETRYABLE(status) || Date.now() + delay > deadline) throw err;

      await new Promise((r) => setTimeout(r, delay));
    }
  }
}

/**
 * Resolves once the session is in the given group, e.g. right after registering it or joining it.
 * Auth learns about new memberships asynchronously, so this switches the session as soon as it can.
 */
export function waitForGroupContext(groupId: string, { timeoutMs = 15000 } = {}): Promise<void> {
  let inflight = contextInflight.get(groupId);

  if (!inflight) {
    inflight = switchToGroupContext(groupId, timeoutMs).finally(() => contextInflight.delete(groupId));
    contextInflight.set(groupId, inflight);
  }

  return inflight;
}
